import type { AxiosError } from 'axios';
import type {
  AxiosErrorCode,
  FlowErrorCode,
  OryServiceErrorCode,
  HttpStatusCode,
  HttpFallbackCode,
  OrySemanticErrorCode,
} from './error-codes';

/**
 * Four focused error types:
 * 1. OryNetworkError - Network/HTTP failures (timeouts, 500s, unreachable)
 * 2. OryBrowserRedirectError - Browser redirect required (expired/replaced/OAuth2 flow)
 * 3. OryFlowValidationError - Flow validation failed (form submission rejected)
 * 4. OryServiceError - Service-level errors (session, CSRF, auth issues)
 *
 * Note: OryFlowValidationError contains the flow with validation errors.
 * Use OryFlowData.hasErrors() and OryFlowData.fieldErrors() to access them.
 */


// V8-specific Error.captureStackTrace (Node.js, Chrome, Edge)
// Safe to call - will be ignored in other environments
declare global {
  interface ErrorConstructor {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    captureStackTrace(targetObject: object, constructorOpt?: new (...args: any[]) => any): void;
  }
}


/**
 * Network or HTTP-level errors
 *
 * Thrown when:
 * - Network timeout or unreachable (no response)
 * - Server returns 500+ errors
 * - Malformed response
 *
 * @example
 * ```typescript
 * catchError((error: Error) => {
 *   if (error instanceof OryNetworkError) {
 *     if (error.retryable) {
 *       showRetryButton();
 *     }
 *     showToast('Network error, please try again');
 *   }
 *   return throwError(() => error);
 * })
 * ```
 */
export class OryNetworkError extends Error {
  override readonly name = 'OryNetworkError';

  constructor(
    public readonly status: number | undefined,
    message: string,
    public readonly url: string,
    public readonly original: AxiosError,
    public readonly retryable = true,
    public readonly timestamp = new Date().toISOString(),
    public readonly code?: AxiosErrorCode, // Type-safe Axios error code
    public readonly requestId?: string // Request correlation ID (if available from headers)
  ) {
    super(message);

    // Set the prototype explicitly to maintain instanceof checks
    Object.setPrototypeOf(this, OryNetworkError.prototype);

    // Capture stack trace (V8/Chrome/Node.js)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, OryNetworkError);
    }
  }

  /**
   * Whether this error was caused by request cancellation (e.g. navigation aborting in-flight requests).
   *
   * @remarks
   * Cancelled requests should not trigger user-facing error UI (e.g. "service temporarily unavailable").
   * The operation was aborted intentionally, not due to a real network failure.
   */
  get isRequestAborted(): boolean {
    return this.code === 'ERR_CANCELED';
  }

  /**
   * Serialize for logging/Sentry - exposes all custom properties
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      stack: this.stack,
      status: this.status,
      url: this.url,
      retryable: this.retryable,
      requestId: this.requestId,
      timestamp: this.timestamp,
      // Don't include full original AxiosError to avoid circular refs
      originalError: {
        message: this.original.message,
        code: this.original.code,
        status: this.original.status,
      },
    };
  }
}

/**
 * Browser redirect required to complete Ory flow
 *
 * Thrown when Ory requires a browser location change:
 * - Flow expired (TTL exceeded) - typically 410 Gone with `use_flow_id`
 * - Flow replaced (ErrorFlowReplaced) - new flow created
 * - Browser location change required (422) - OAuth2/consent flow continuation
 *
 * This is EXPECTED behavior in Ory's flow lifecycle.
 * Always redirect to the provided URL.
 *
 * @remarks
 * Aligns with Ory's `ErrorBrowserLocationChangeRequired` type.
 * The `code` property preserves Ory's original error ID for differentiation.
 *
 * @example
 * ```typescript
 * catchError((error: Error) => {
 *   if (error instanceof OryBrowserRedirectError) {
 *     return this.router.navigateByUrl(error.redirectTo);
 *   }
 *   return throwError(() => error);
 * })
 * ```
 */
export class OryBrowserRedirectError extends Error {
  override readonly name = 'OryBrowserRedirectError';

  constructor(
    public readonly redirectTo: string,
    public readonly code: OryServiceErrorCode | FlowErrorCode = 'browser_location_change_required',
    public readonly httpStatus = 422,
    public readonly requestId?: string, // Request correlation ID
    public readonly timestamp = new Date().toISOString()
  ) {
    super(`Browser redirect required: ${redirectTo}`);

    // Set the prototype explicitly to maintain instanceof checks
    Object.setPrototypeOf(this, OryBrowserRedirectError.prototype);

    // Capture stack trace (V8/Chrome/Node.js)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, OryBrowserRedirectError);
    }
  }

  /**
   * Serialize for logging/Sentry - exposes all custom properties
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      stack: this.stack,
      redirectTo: this.redirectTo,
      httpStatus: this.httpStatus,
      requestId: this.requestId,
      timestamp: this.timestamp,
    };
  }
}

/**
 * Flow validation error - form submission was rejected due to validation errors
 *
 * Thrown when:
 * - User submits a flow with validation errors (weak password, invalid email, etc.)
 * - Server returns 400 Bad Request with the updated flow containing ui.messages
 * - Server returns 422 Unprocessable Entity with flow data (SPAs)
 *
 * This is EXPECTED user behavior during form submission.
 * Re-render the form using the provided flow object and display error messages.
 *
 * The flow object contains:
 * - `ui.messages` - Global validation errors
 * - `ui.nodes[].messages` - Field-specific validation errors
 *
 * The error message automatically includes Ory's global error messages for better debugging.
 * For accessing individual error objects, use OryFlowData.getMessages() helper.
 *
 * @example
 * ```typescript
 * catchError((error: Error) => {
 *   if (error instanceof OryFlowValidationError) {
 *     // Error message automatically includes: "The provided credentials are invalid"
 *     console.error(error.message); // Human-readable Ory message!
 *
 *     // Update the form with the new flow
 *     this.flow.set(error.flow);
 *
 *     // Extract detailed errors using OryFlowData helper (recommended)
 *     const globalErrors = OryFlowData.getMessages(error.flow, {
 *       types: ['error'],
 *       scope: 'global'
 *     });
 *     globalErrors.forEach(err => showToast(err.text));
 *     return EMPTY;
 *   }
 *   return throwError(() => error);
 * })
 * ```
 */
export class OryFlowValidationError<FlowData = unknown> extends Error {
  override readonly name = 'OryFlowValidationError';

  constructor(
    public readonly flow: FlowData,
    public readonly httpStatus: number,
    public readonly code: OrySemanticErrorCode | FlowErrorCode = 'flow_validation_error',
    errorMessage: string,
    public readonly requestId?: string, // Ory's correlation ID for request tracing
    public readonly timestamp = new Date().toISOString()
  ) {
    super(errorMessage);

    // Set the prototype explicitly to maintain instanceof checks
    Object.setPrototypeOf(this, OryFlowValidationError.prototype);

    // Capture stack trace (V8/Chrome/Node.js)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, OryFlowValidationError);
    }
  }

  /**
   * Serialize for logging/Sentry - exposes all custom properties
   *
   * Note: Flow object is included for debugging. Extract error messages
   * using OryFlowData.getMessages(error.flow, options)
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      stack: this.stack,
      flow: this.flow,
      httpStatus: this.httpStatus,
      requestId: this.requestId,
      timestamp: this.timestamp,
    };
  }
}

/**
 * Service-level authentication/authorization errors from Ory Kratos
 *
 * Uses Ory's native error.id directly - no custom mapping.
 * Common Ory error IDs include:
 * - 'session_inactive' - No valid session (401)
 * - 'session_refresh_required' - Reauthentication needed (privileged action)
 * - 'security_csrf_violation' - Invalid or missing CSRF token (403)
 * - 'self_service_flow_expired' - Flow has expired
 * - 'browser_location_change_required' - Redirect needed
 *
 * See Ory Kratos docs for complete list: https://www.ory.com/docs/kratos/reference/api
 *
 * @example
 * ```typescript
 * catchError((error: Error) => {
 *   if (error instanceof OryServiceError) {
 *     // Log for debugging
 *     console.error('Ory Service Error:', {
 *       code: error.code,
 *       requestId: error.requestId,
 *       reason: error.reason
 *     });
 *
 *     switch (error.code) {
 *       case 'session_inactive':
 *         return this.router.navigate(['/login']);
 *       case 'session_refresh_required':
 *         return this.router.navigate(['/reauthenticate']);
 *       case 'security_csrf_violation':
 *         return this.createNewFlow();
 *     }
 *   }
 *   return throwError(() => error);
 * })
 * ```
 */
export class OryServiceError extends Error {
  override readonly name = 'OryServiceError';

  constructor(
    public readonly code: OryServiceErrorCode | HttpStatusCode | HttpFallbackCode, // Type-safe error code with hierarchy
    message: string,
    public readonly httpStatus: number,
    public readonly requestId?: string, // Ory's correlation ID for support/debugging
    public readonly reason?: string, // Human-readable reason from Ory
    public readonly debug?: string, // Debug info (only in development)
    public readonly timestamp = new Date().toISOString()
  ) {
    super(message);

    // Set the prototype explicitly to maintain instanceof checks
    Object.setPrototypeOf(this, OryServiceError.prototype);

    // Capture stack trace (V8/Chrome/Node.js)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, OryServiceError);
    }
  }

  /**
   * Serialize for logging/Sentry - exposes all custom properties
   *
   * Note: Use requestId to correlate with Ory backend logs
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      stack: this.stack,
      code: this.code,
      httpStatus: this.httpStatus,
      requestId: this.requestId,
      reason: this.reason,
      debug: this.debug,
      timestamp: this.timestamp,
    };
  }
}

import { UiContainer } from '@ory/client';
import {
  ErrorAuthenticatorAssuranceLevelNotSatisfied,
  ErrorBrowserLocationChangeRequired,
  ErrorFlowReplaced,
  ErrorOAuth2,
  FlowError,
  NeedsPrivilegedSessionError,
  ParseError,
  PostCheckPermissionOrErrorBody,
  SelfServiceFlowExpiredError
} from '@ory/client/api';

interface ErrorFlowUI {
  ui: UiContainer;
}

export type OryProviderError = ErrorAuthenticatorAssuranceLevelNotSatisfied
  | ErrorBrowserLocationChangeRequired
  | ErrorFlowReplaced
  | ErrorOAuth2
  | FlowError
  | ErrorFlowUI
  | NeedsPrivilegedSessionError
  | ParseError
  | PostCheckPermissionOrErrorBody
  | SelfServiceFlowExpiredError;
